import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Slot } from "@radix-ui/react-slot";
import { Button } from "./button";
import { cn } from "../../lib/utils";

const SidebarContext = createContext(null);

export function SidebarProvider({
  children,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  openMobile: controlledMobileOpen = false,
  onOpenMobileChange,
  isMobile = false,
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;

  const setOpen = useCallback((nextValue) => {
    const next = typeof nextValue === "function" ? nextValue(open) : nextValue;
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  }, [controlledOpen, onOpenChange, open]);

  const setOpenMobile = useCallback((nextValue) => {
    const next = typeof nextValue === "function" ? nextValue(controlledMobileOpen) : nextValue;
    onOpenMobileChange?.(next);
  }, [controlledMobileOpen, onOpenMobileChange]);

  const value = useMemo(() => ({
    open,
    setOpen,
    openMobile: controlledMobileOpen,
    setOpenMobile,
    isMobile,
    state: open ? "expanded" : "collapsed",
    toggleSidebar: () => (isMobile ? setOpenMobile((current) => !current) : setOpen((current) => !current)),
  }), [open, setOpen, controlledMobileOpen, setOpenMobile, isMobile]);

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) throw new Error("useSidebar must be used within SidebarProvider");
  return context;
}

export const Sidebar = React.forwardRef(({ className, side = "left", collapsible = "icon", ...props }, ref) => {
  const { open, openMobile, isMobile } = useSidebar();
  return (
    <aside
      ref={ref}
      data-side={side}
      data-collapsible={collapsible}
      data-state={isMobile ? (openMobile ? "expanded" : "collapsed") : (open ? "expanded" : "collapsed")}
      className={cn("group/sidebar", className)}
      {...props}
    />
  );
});
Sidebar.displayName = "Sidebar";

export const SidebarInset = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("relative min-h-svh min-w-0 flex-1", className)} {...props} />
));
SidebarInset.displayName = "SidebarInset";

export const SidebarHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("shrink-0", className)} {...props} />
));
SidebarHeader.displayName = "SidebarHeader";

export const SidebarContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("min-h-0 flex-1 overflow-auto", className)} {...props} />
));
SidebarContent.displayName = "SidebarContent";

export const SidebarFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("mt-auto shrink-0", className)} {...props} />
));
SidebarFooter.displayName = "SidebarFooter";

export const SidebarMenu = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-1", className)} role="list" {...props} />
));
SidebarMenu.displayName = "SidebarMenu";

export const SidebarMenuItem = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("min-w-0", className)} role="listitem" {...props} />
));
SidebarMenuItem.displayName = "SidebarMenuItem";

export const SidebarMenuButton = React.forwardRef(({ className, asChild = false, isActive = false, ...props }, ref) => {
  const Component = asChild ? Slot : "button";
  return (
    <Component
      ref={ref}
      data-active={isActive}
      className={cn("flex w-full items-center gap-3 rounded-md text-start transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50", className)}
      {...props}
    />
  );
});
SidebarMenuButton.displayName = "SidebarMenuButton";

export function SidebarTrigger({ className, onClick, children, ...props }) {
  const { toggleSidebar, open, openMobile, isMobile } = useSidebar();
  return (
    <Button
      variant="ghost"
      size="icon"
      className={className}
      aria-expanded={isMobile ? openMobile : open}
      onClick={(event) => {
        if (onClick) onClick(event);
        else toggleSidebar();
      }}
      {...props}
    >
      {children}
    </Button>
  );
}
